import { Injectable } from '@angular/core';
import { IconName } from '@webapp/core/constants/lucide-icons';
import { InquiryServiceType } from '@webapp/core/enums';
import {
  CreateInquiryStep2,
  CreateInquiryStep2_1,
  CreateInquiryStep2_5,
  CreateInquiryStep3,
  CreateInquiryStep3_5,
  CreateInquiryStep4,
  InquiryCreationProgress,
  IInquiry,
  InquiryCreationStep
} from '@webapp/core/interfaces';
import { PublicInquiryService } from 'projects/webapp/app/services/public-inquiry.service';
import { BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';

const PROGRESS_STORAGE_KEY = 'inquiryProgressData';

/** One step of a flow, as the sidebar and the step counter show it. */
export interface InquiryFlowStep {
  step: InquiryCreationStep;
  label: string;
  icon: IconName;
}

/** What a step hands back, unvalidated, when the customer leaves it backwards. */
export type InquiryStepDraft = Partial<Pick<InquiryCreationProgress,
  'step2' | 'step2_1' | 'step2_5' | 'step3' | 'step3_5' | 'step4'>>;

export type InquiryDraftSource = () => InquiryStepDraft;

const AUSZUG: InquiryFlowStep = { step: 2, label: 'Auszug', icon: 'log-out' };
const EINZUG: InquiryFlowStep = { step: 2.5, label: 'Einzug', icon: 'log-in' };
const SERVICES: InquiryFlowStep = { step: 3, label: 'Services', icon: 'package' };
const OBJEKT: InquiryFlowStep = { step: 2.1, label: 'Objekt', icon: 'house' };
const REINIGUNG: InquiryFlowStep = { step: 3.5, label: 'Reinigung', icon: 'brush' };
const TERMIN_UND_KONTAKT: InquiryFlowStep = { step: 4, label: 'Termin & Kontakt', icon: 'mail' };

/**
 * The steps of each flow, in order — the one source for the navigation, the
 * sidebar and the "Schritt X von Y" counter.
 */
const FLOW_STEPS: Record<InquiryServiceType, InquiryFlowStep[]> = {
  [InquiryServiceType.MOVE]: [AUSZUG, EINZUG, SERVICES, TERMIN_UND_KONTAKT],
  [InquiryServiceType.CLEAN]: [OBJEKT, REINIGUNG, TERMIN_UND_KONTAKT],
  [InquiryServiceType.MOVE_AND_CLEAN]: [AUSZUG, EINZUG, SERVICES, REINIGUNG, TERMIN_UND_KONTAKT],
};

@Injectable({ providedIn: 'root' })
export class PublicInquiryManagerService {
  protected _isInited = false;
  protected _inquiryCreationProgress$ = new BehaviorSubject<InquiryCreationProgress | null>(null);
  protected _draftSource: InquiryDraftSource | null = null;

  get inquiryCreationProgress$() {
    return this._inquiryCreationProgress$.asObservable();
  }

  get inquiryCreationProgress() {
    return this._inquiryCreationProgress$.value;
  }

  constructor(
    protected _publicInquiryService: PublicInquiryService,
  ) {
  }

  init() {
    if (this._isInited) return;
    this._isInited = true;

    const data = localStorage.getItem(PROGRESS_STORAGE_KEY);
    if (!data) {
      this._inquiryCreationProgress$.next(null);
    } else {
      try {
        const inquiryProgressData = JSON.parse(data) as InquiryCreationProgress
        this._inquiryCreationProgress$.next(inquiryProgressData);
      } catch {
        this._inquiryCreationProgress$.next(null);
      }
    }

    // The summary step is persisted too: it holds the uuid, so a reload keeps
    // the customer on their own inquiry instead of an empty form.
    this._inquiryCreationProgress$.subscribe((data) => {
      if (data) localStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(data));
      else localStorage.removeItem(PROGRESS_STORAGE_KEY);
    })
  }

  resetProgress() {
    this._inquiryCreationProgress$.next(null);
  }

  /**
   * Forget the stored progress without moving the customer off their screen.
   *
   * Called once the correction window has closed: the inquiry is final, so the
   * next visit to /inquiry should start a fresh form — but the summary the
   * customer is looking at must keep rendering.
   */
  forgetPersistedProgress() {
    localStorage.removeItem(PROGRESS_STORAGE_KEY);
  }

  /**
   * The inquiry as it was submitted, merged from the collected steps.
   *
   * The summary screen normally shows what the API returns. This is the
   * fallback for the one case where it cannot: a customer who comes back after
   * the window closed, when the endpoint answers 410 but their own data is
   * still worth showing.
   */
  submittedInquiryData(): Partial<IInquiry> | null {
    const progressData = this._inquiryCreationProgress$.value;
    if (!progressData) return null;

    // The step payloads already use the API's field names, so this doubles
    // as a read model for the summary.
    return this._getCreateBody(progressData) as Partial<IInquiry>;
  }

  /** The steps of a flow, in order. Without an argument: the flow in progress. */
  flowSteps(service = this._inquiryCreationProgress$.value?.service): InquiryFlowStep[] {
    return service ? FLOW_STEPS[service] ?? [] : [];
  }

  /**
   * Let the step on screen hand over what has been typed so far when the
   * customer leaves it backwards — "Zurück" or a done step in the sidebar.
   * Forward only goes through the step's own validated submit.
   */
  setDraftSource(source: InquiryDraftSource) {
    this._draftSource = source;
  }

  /**
   * Only clears `source` if it is still the registered one: when steps swap,
   * the entering step can register before the leaving one is destroyed.
   */
  clearDraftSource(source: InquiryDraftSource) {
    if (this._draftSource === source) this._draftSource = null;
  }

  submitStep1(service: InquiryServiceType, tenant?: string | null) {
    const currentProgressData = this._inquiryCreationProgress$.value;
    if (currentProgressData && currentProgressData.currentStep !== 1) {
      throw new Error('Inquiry creation progress is not in step 1. Cannot submit step 1.')
    }
    const currentStep = this._nextStep(1, service);
    this._inquiryCreationProgress$.next({ ...currentProgressData, currentStep, service, tenant });
  }

  submitStep2(data: CreateInquiryStep2) {
    const currentProgressData = this._inquiryCreationProgress$.value;
    if (currentProgressData?.currentStep !== 2) {
      throw new Error('Inquiry creation progress is not in step 2. Cannot submit step 2.')
    }
    const currentStep = this._nextStep(2, currentProgressData.service);
    this._inquiryCreationProgress$.next({ ...currentProgressData, currentStep, step2: data })
  }

  submitStep2_1(data: CreateInquiryStep2_1) {
    const currentProgressData = this._inquiryCreationProgress$.value;
    if (currentProgressData?.currentStep !== 2.1) {
      throw new Error('Inquiry creation progress is not in step 2.1. Cannot submit step 2.1.')
    }
    const currentStep = this._nextStep(2.1, currentProgressData.service);
    this._inquiryCreationProgress$.next({ ...currentProgressData, currentStep, step2_1: data })
  }

  submitStep2_5(data: CreateInquiryStep2_5) {
    const currentProgressData = this._inquiryCreationProgress$.value;
    if (currentProgressData?.currentStep !== 2.5) {
      throw new Error('Inquiry creation progress is not in step 2.5. Cannot submit step 2.5.')
    }
    if (!currentProgressData.step2) {
      throw new Error('Step 2 data is missing. Cannot submit step 2.5.')
    }
    const currentStep = this._nextStep(2.5, currentProgressData.service);
    this._inquiryCreationProgress$.next({ ...currentProgressData, currentStep, step2_5: data })
  }

  submitStep3(data: CreateInquiryStep3) {
    const currentProgressData = this._inquiryCreationProgress$.value;
    if (currentProgressData?.currentStep !== 3) {
      throw new Error('Inquiry creation progress is not in step 3. Cannot submit step 3.')
    }
    const serviceType = currentProgressData.service;
    const isMoving = serviceType === InquiryServiceType.MOVE || serviceType === InquiryServiceType.MOVE_AND_CLEAN;
    if (isMoving && (!currentProgressData?.step2 || !currentProgressData?.step2_5)) {
      throw new Error('Step 2 / 2.5 data is missing. Cannot submit step 3.')
    }
    if (!isMoving && !currentProgressData?.step2_1) {
      throw new Error('Step 2.1 data is missing. Cannot submit step 3.')
    }
    const currentStep = this._nextStep(3, serviceType);
    this._inquiryCreationProgress$.next({ ...currentProgressData, currentStep, step3: data })
  }

  submitStep3_5(data: CreateInquiryStep3_5) {
    const currentProgressData = this._inquiryCreationProgress$.value;
    if (currentProgressData?.currentStep !== 3.5) {
      throw new Error('Inquiry creation progress is not in step 3.5. Cannot submit step 3.5.')
    }

    const serviceType = currentProgressData.service;
    if (serviceType === InquiryServiceType.CLEAN && !currentProgressData?.step2_1) {
      throw new Error('Step 2.1 data is missing. Cannot submit step 3.5.')
    } else if (serviceType === InquiryServiceType.MOVE_AND_CLEAN
      && (!currentProgressData?.step2 || !currentProgressData?.step2_5)) {
      throw new Error('Step 2 / 2.5 data is missing. Cannot submit step 3.5.')
    }

    if (serviceType === InquiryServiceType.MOVE_AND_CLEAN && !currentProgressData?.step3) {
      throw new Error('Step 3 data is missing. Cannot submit step 3.5.')
    }

    const currentStep = this._nextStep(3.5, currentProgressData.service);
    this._inquiryCreationProgress$.next({ ...currentProgressData, currentStep, step3_5: data })
  }

  submitStep4(data: CreateInquiryStep4, captchaToken: string) {
    const currentProgressData = this._inquiryCreationProgress$.value;
    if (currentProgressData?.currentStep !== 4) {
      throw new Error('Inquiry creation progress is not in step 4. Cannot submit step 4.')
    }
    const serviceType = currentProgressData.service;
    const isMoving = serviceType === InquiryServiceType.MOVE || serviceType === InquiryServiceType.MOVE_AND_CLEAN;
    const isCleaning = serviceType === InquiryServiceType.CLEAN || serviceType === InquiryServiceType.MOVE_AND_CLEAN;
    if (isMoving && (!currentProgressData?.step2 || !currentProgressData?.step2_5)) {
      throw new Error('Step 2 / 2.5 data is missing. Cannot submit step 4.')
    }
    if (serviceType === InquiryServiceType.CLEAN && !currentProgressData?.step2_1) {
      throw new Error('Step 2.1 data is missing. Cannot submit step 4.')
    }

    if (isMoving && !currentProgressData?.step3) {
      throw new Error('Step 3 data is missing. Cannot submit step 4.')
    }
    if (isCleaning && !currentProgressData?.step3_5) {
      throw new Error('Step 3.5 data is missing. Cannot submit step 4.')
    }

    // The API rejects an empty string for a date it treats as optional.
    const step4 = { ...data, handover_date: data.handover_date || null };
    const progress: InquiryCreationProgress = { ...currentProgressData, step4 };
    this._inquiryCreationProgress$.next(progress)
    const currentStep = this._nextStep(4, progress.service);
    return this._publicInquiryService
      .createEnquiry({ ...this._getCreateBody(progress), captcha_token: captchaToken })
      .pipe(tap({
        next: ({ uuid, dispatch_after }) => this._inquiryCreationProgress$.next({
          ...progress, currentStep, uuid, dispatchAfter: dispatch_after,
        })
      }))
  }

  goPrevStep() {
    const progressData = this._inquiryCreationProgress$.value;
    if (!progressData) return;

    const currentStep = this._prevStep(progressData.currentStep, progressData.service);
    if (currentStep === progressData.currentStep) return;
    this._inquiryCreationProgress$.next({ ...progressData, ...this._collectDraft(), currentStep })
  }

  /**
   * Jump back to a step already done in the current flow. Anything else — a
   * later step, a step of another flow, anything from the summary — is ignored:
   * forward only goes through the validated "Weiter".
   */
  goToStep(step: InquiryCreationStep) {
    const progressData = this._inquiryCreationProgress$.value;
    if (!progressData || progressData.currentStep === 'summary') return;

    const steps = this.flowSteps(progressData.service).map(s => s.step);
    const target = steps.indexOf(step);
    const current = steps.indexOf(progressData.currentStep);
    if (target < 0 || current < 0 || target >= current) return;

    this._inquiryCreationProgress$.next({ ...progressData, ...this._collectDraft(), currentStep: step })
  }

  protected _collectDraft(): InquiryStepDraft {
    return this._draftSource?.() ?? {};
  }

  protected _nextStep(currentStep: InquiryCreationStep, serviceType: InquiryServiceType): InquiryCreationStep {
    if (currentStep === 'summary') return 'summary';

    const steps = this.flowSteps(serviceType);
    if (currentStep === 1) return steps[0]?.step ?? 1;

    const index = steps.findIndex(s => s.step === currentStep);
    if (index < 0) return currentStep;
    return steps[index + 1]?.step ?? 'summary';
  }

  protected _prevStep(currentStep: InquiryCreationStep, serviceType: InquiryServiceType): InquiryCreationStep {
    // The inquiry exists once the summary is reached; stepping back into the
    // form would create a second one. Corrections happen on the summary itself.
    if (currentStep === 'summary' || currentStep === 1) return currentStep;

    const steps = this.flowSteps(serviceType);
    const index = steps.findIndex(s => s.step === currentStep);
    if (index < 0) return currentStep;
    return index === 0 ? 1 : steps[index - 1].step;
  }

  protected _getCreateBody(currentProgressData: InquiryCreationProgress) {
    if (currentProgressData.service === InquiryServiceType.CLEAN) return {
      service: currentProgressData.service,
      tenant: currentProgressData.tenant,
      ...currentProgressData.step2_1,
      ...currentProgressData.step3_5,
      ...currentProgressData.step4,
    }
    if (currentProgressData.service === InquiryServiceType.MOVE_AND_CLEAN) return {
      service: currentProgressData.service,
      tenant: currentProgressData.tenant,
      ...currentProgressData.step2,
      ...currentProgressData.step2_5,
      ...currentProgressData.step3,
      ...currentProgressData.step3_5,
      ...currentProgressData.step4,
    }
    return {
      service: currentProgressData.service,
      tenant: currentProgressData.tenant,
      ...currentProgressData.step2,
      ...currentProgressData.step2_5,
      ...currentProgressData.step3,
      ...currentProgressData.step4,
    }
  }
}
